import { useState, type ReactNode } from "react";
import { ArrowUpRight, Check, Minus } from "lucide-react";
import { BookmakerLogo } from "../components/BookmakerCard";
import { SEO } from "../components/SEO";
import { SectionHeading } from "../components/ui";
import { useCountry } from "../context/CountryContext";
import { bookmakers } from "../data/demo";

export function ComparePage() {
  const { country } = useCountry();
  const available =
    country.code === "global"
      ? bookmakers
      : bookmakers.filter((item) => item.countries.includes(country.code));
  const [selected, setSelected] = useState<string[]>(
    available.slice(0, 3).map((item) => item.id),
  );
  const compared = bookmakers.filter((item) => selected.includes(item.id));
  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id)
        ? current.length > 2
          ? current.filter((item) => item !== id)
          : current
        : current.length < 3
          ? [...current, id]
          : current,
    );
  const rows: Array<{ label: string; render: (id: string) => ReactNode }> = [
    {
      label: "Editorial rating",
      render: (id) => (
        <strong className="text-lg text-lime">
          {bookmakers.find((item) => item.id === id)?.rating}/10
        </strong>
      ),
    },
    {
      label: "Minimum deposit",
      render: (id) =>
        bookmakers.find((item) => item.id === id)?.minimumDeposit[
          country.code
        ] || "Check local terms",
    },
    {
      label: "Withdrawals",
      render: (id) => bookmakers.find((item) => item.id === id)?.withdrawalTime,
    },
    {
      label: "Payments",
      render: (id) =>
        bookmakers.find((item) => item.id === id)?.paymentMethods.join(", "),
    },
    {
      label: "Sports",
      render: (id) =>
        bookmakers.find((item) => item.id === id)?.sports.join(", "),
    },
    {
      label: "Live betting",
      render: (id) =>
        bookmakers.find((item) => item.id === id)?.liveBetting ? (
          <Check className="mx-auto text-lime" />
        ) : (
          <Minus className="mx-auto text-muted" />
        ),
    },
    {
      label: "Cashout",
      render: (id) =>
        bookmakers.find((item) => item.id === id)?.cashout ? (
          <Check className="mx-auto text-lime" />
        ) : (
          <Minus className="mx-auto text-muted" />
        ),
    },
    {
      label: "Mobile experience",
      render: (id) =>
        `${bookmakers.find((item) => item.id === id)?.scores.mobile}/10`,
    },
    {
      label: "Current sample offer",
      render: (id) => bookmakers.find((item) => item.id === id)?.promotion,
    },
  ];
  return (
    <div className="page-shell py-10 sm:py-14">
      <SEO
        title="Compare betting sites"
        description="Compare betting-site ratings, deposits, withdrawals, payments and features side by side."
        path="/compare"
      />
      <SectionHeading
        headingLevel={1}
        eyebrow="Side-by-side"
        title="Compare betting sites"
        description="Choose two or three sites. Availability and account terms still need confirmation with the operator."
      />
      <div className="mb-6 flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
        {available.map((bookmaker) => (
          <button
            onClick={() => toggle(bookmaker.id)}
            key={bookmaker.id}
            className={`shrink-0 rounded-xl border px-4 py-3 text-sm font-bold ${selected.includes(bookmaker.id) ? "border-lime/50 bg-lime/10 text-lime" : "border-soft bg-white/[.025] text-muted"}`}
          >
            {bookmaker.name}
          </button>
        ))}
      </div>
      <div className="surface overflow-x-auto rounded-2xl">
        <div className="min-w-[680px]">
          <div
            className="grid border-b border-soft bg-white/[.02]"
            style={{
              gridTemplateColumns: `180px repeat(${compared.length}, minmax(160px, 1fr))`,
            }}
          >
            <div className="p-4" />
            {compared.map((bookmaker) => (
              <div className="p-4" key={bookmaker.id}>
                <BookmakerLogo bookmaker={bookmaker} />
              </div>
            ))}
          </div>
          {rows.map((row) => (
            <div
              key={row.label}
              className="grid border-b border-soft last:border-0"
              style={{
                gridTemplateColumns: `180px repeat(${compared.length}, minmax(160px, 1fr))`,
              }}
            >
              <div className="bg-white/[.015] p-4 text-xs font-bold uppercase tracking-wider text-muted">
                {row.label}
              </div>
              {compared.map((bookmaker) => (
                <div
                  className="border-l border-soft p-4 text-center text-sm"
                  key={bookmaker.id}
                >
                  {row.render(bookmaker.id)}
                </div>
              ))}
            </div>
          ))}
          <div
            className="grid"
            style={{
              gridTemplateColumns: `180px repeat(${compared.length}, minmax(160px, 1fr))`,
            }}
          >
            <div className="p-4 text-xs text-muted">Affiliate links</div>
            {compared.map((bookmaker) => {
              const url =
                country.code === "global"
                  ? undefined
                  : bookmaker.affiliateUrls[country.code];
              return (
                <div className="border-l border-soft p-4" key={bookmaker.id}>
                  {url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="nofollow sponsored noopener"
                      className="flex items-center justify-center gap-1 rounded-xl bg-lime px-3 py-2.5 text-sm font-extrabold"
                    >
                      Visit site <ArrowUpRight size={14} />
                    </a>
                  ) : (
                    <span className="block text-center text-xs text-muted">
                      No verified destination
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <p className="mt-4 text-xs leading-5 text-muted">
        Editorial comparisons are independent. We may receive commission from
        affiliate links; sponsored placements are labelled separately.
      </p>
    </div>
  );
}
